import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

// Drag-and-drop / click / paste upload area. Owns its own file input, drag
// tracking and paste handling so callers only receive the files.
const Zone = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--gap-m);
    padding: 26px 20px;
    border: 1px dashed ${(props) => (props.$dragging ? "var(--pw-brand-cyan)" : "var(--border-strong)")};
    border-radius: var(--pw-radius-lg);
    background: ${(props) => (props.$dragging ? "var(--pw-bg-app)" : "var(--surface-tile)")};
    cursor: pointer;
    text-align: center;
    transition: background var(--pw-transition) var(--pw-ease),
        border-color var(--pw-transition) var(--pw-ease);

    &:hover {
        border-color: var(--pw-brand-cyan);
        background: var(--pw-bg-app);
    }

    &:focus-visible {
        outline: 2px solid var(--pw-focus-ring);
        outline-offset: 2px;
    }
`;

const Icon = styled.i`
    font-size: 20px;
    color: var(--text-subtle);
`;

const Hint = styled.span`
    font-size: var(--pw-text-base);
    font-weight: var(--pw-weight-light);
    color: var(--text-muted);
`;

const HiddenInput = styled.input`
    display: none;
`;

export const PathwayDropzone = ({
    onFiles,
    accept = "image/*",
    multiple = true,
    hint = "Bilder hierher ziehen, klicken oder einfügen (Strg+V)",
    draggingHint = "Dateien hier ablegen",
    icon = "fa fa-cloud-upload",
    dataCy,
}) => {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    const zoneRef = useRef(null);

    const emit = (fileList) => {
        const files = Array.from(fileList || []);
        if (files.length > 0) {
            onFiles(files);
        }
    };

    const openPicker = () => inputRef.current?.click();

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        // Ignore drags moving between children of the zone.
        if (zoneRef.current && !zoneRef.current.contains(event.relatedTarget)) {
            setDragging(false);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setDragging(false);
        emit(event.dataTransfer?.files);
    };

    const handlePaste = (event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const pasted = items.filter((item) => item.kind === "file").map((item) => item.getAsFile());
        emit(pasted.filter(Boolean));
    };

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPicker();
        }
    };

    return (
        <Zone
            ref={zoneRef}
            $dragging={dragging}
            role="button"
            tabIndex={0}
            onClick={openPicker}
            onKeyDown={handleKeyDown}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onPaste={handlePaste}
            data-cy={dataCy}
        >
            <HiddenInput
                type="file"
                accept={accept}
                multiple={multiple}
                ref={inputRef}
                onChange={(event) => {
                    emit(event.target.files);
                    if (inputRef.current) {
                        inputRef.current.value = "";
                    }
                }}
                data-cy={dataCy ? dataCy + "-input" : undefined}
            />
            <Icon className={icon} aria-hidden="true" />
            <Hint>{dragging ? draggingHint : hint}</Hint>
        </Zone>
    );
};

PathwayDropzone.propTypes = {
    onFiles: PropTypes.func.isRequired,
    accept: PropTypes.string,
    multiple: PropTypes.bool,
    hint: PropTypes.string,
    draggingHint: PropTypes.string,
    icon: PropTypes.string,
    dataCy: PropTypes.string,
};

export default PathwayDropzone;
